import { CallStatus } from '@act/shared/definitions';
import React, { useContext } from 'react';

import { ResourceDto } from '@act/shared/data-transfer-objects';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';
import { Box, Button as MuiButton, CardActions, Tooltip } from '@mui/material';
import { Button } from '@act/storybook';
import Typography from '@mui/material/Typography';
import { HtmlDisplayerWithHyperlinks } from 'react-components/components/HtmlDisplayerWithHyperlinks';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import useMemberStatusChangeRequestDialog from 'react-components/dialog-forms/hooks/use-member-status-change-request-dialog';
import { NeedMiniInfoBox } from 'react-components/needs/components/need-mini-info';
import { selectSelectedMember } from 'redux/slices/memberSlice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hooks';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { callsAppSelectors } from 'redux/slices/callsSlice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { needsSelectors } from 'redux/slices/needs.slice';
import { useAppSelector } from 'redux/store-hooks';
import { utilityAppSelectors } from 'redux/utils/selectors/utility-selectors';
import InfoCard, {
  AdditionalFeedCardActions,
} from 'toolkit/info-card/info-card';
import { reemaColors } from 'toolkit/styles/colors';
import { phoneFormatter } from 'utilities/formatters/phone-formatter';
import { canMakeRequest } from './feed-card-utility';
import { FeedVoicemail } from './feed-voicemail';
import { Permission } from '@act/shared/authorization';
import { twilioVoiceErrorMap } from 'redux/types/shared-types';
import { hasPermission } from 'utilities/hasPermissions';
import { handleConnectionLogEntries } from './callConnectionLogEntries';
import PostCallSummaryContext from 'react-components/member-page/post-call-summary-drawer/PostCallSummaryContext';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import { VoicemailIcon } from '@phosphor-icons/react';
import { TaggableChip } from '@act/storybook';
import { makeRequest } from 'redux/utils/api-request';
import { filesRequests } from 'redux/requests/files.requests';
import { useTracking } from 'react-components/tracking';
import { useRef, useEffect } from 'react';

const FeedCallCard = ({
  callId,
  isLastEventItem,
  shouldHighlight,
}: {
  callId: number;
  isLastEventItem: boolean;
  shouldHighlight?: boolean;
}) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const call = callsAppSelectors.useSelectCallById(callId);
  const callDataQualityFF = useFeatureFlag('call-data-quality');
  const needsCallFF = useFeatureFlag('needs-call');
  const { setVoiceCallId } = useContext(PostCallSummaryContext);
  const tracking = useTracking();
  const { generateTrackingProps } = tracking || {};
  const outcome = metaSliceSelectors.useSelectCallOutcomeById(call?.outcomeId);
  const member = membersAppSelectors.useSelectMemberById(call?.subjectId);
  const selectedMember = useAppSelector(selectSelectedMember);
  const isAdmin = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ACCESS_ADMIN_TOOLS
  );
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const caller = utilityAppSelectors.selectUserOrMemberById(
    call?.originator?.userId
  );
  const receiver = utilityAppSelectors.selectUserOrMemberById(
    call?.receiver?.userId
  );

  // Calculate needId before hooks that depend on it
  // Use cached data if available to prevent jitter during updates
  let needId: number = null;
  const resourceInteraction = call?.resourceInteraction;
  const needResourceInteraction = call?.needResourceInteraction;

  if (resourceInteraction) {
    needId = resourceInteraction.userNeedId;
  } else if (needResourceInteraction) {
    needId = needResourceInteraction.userNeedId;
  }

  const need = needsSelectors.useSelectNeedById(needId);

  // Cache entire call state to prevent jitter during updates
  // When a call is edited via dialog or websocket, the call object may be temporarily
  // replaced with incomplete data, causing the card to shrink/grow (visual jitter)
  const lastValidCallDataRef = useRef({
    need: need,
    resourceInteraction: resourceInteraction,
    needResourceInteraction: needResourceInteraction,
  });

  useEffect(() => {
    // Update cache only when we have complete data
    if (need) {
      lastValidCallDataRef.current.need = need;
    }
    if (resourceInteraction) {
      lastValidCallDataRef.current.resourceInteraction = resourceInteraction;
    }
    if (needResourceInteraction) {
      lastValidCallDataRef.current.needResourceInteraction =
        needResourceInteraction;
    }
  }, [need, resourceInteraction, needResourceInteraction]);

  // For resourcing calls, use cached data if current data is missing
  const isResourcingCall =
    !!resourceInteraction ||
    !!needResourceInteraction ||
    !!lastValidCallDataRef.current.resourceInteraction ||
    !!lastValidCallDataRef.current.needResourceInteraction;

  const displayNeed =
    need || (isResourcingCall ? lastValidCallDataRef.current.need : null);
  const displayResourceInteraction =
    resourceInteraction || lastValidCallDataRef.current.resourceInteraction;
  const displayNeedResourceInteraction =
    needResourceInteraction ||
    lastValidCallDataRef.current.needResourceInteraction;

  const { editCall, createCall } = useInteractionsDialog();
  const openMemberStatusChangeRequestDialog =
    useMemberStatusChangeRequestDialog();

  // NOW we can do conditional returns after all hooks are called
  if (!call) {
    // It is possible that the call is a feedback call,
    // and the guide does not have scope permissions to view it.
    return null;
  }

  const isResourcing: boolean =
    !!displayResourceInteraction || !!displayNeedResourceInteraction;

  const canManageFeedbackCalls = hasPermission(
    actingUser,
    'CAN_MANAGE_MEMBER_FEEDBACK'
  );

  const statusChips: React.ReactElement[] = [];
  const menuItems: AdditionalFeedCardActions[] = [];
  let secondaryOption: React.ReactElement;

  if (!call.outcomeId && !call.isFeedbackCall) {
    statusChips.push(<TaggableChip isTag color="warning" label="Incomplete" />);
  } else if (call.insufficientNotes) {
    statusChips.push(<TaggableChip isTag color="warning" label="Incomplete" />);
  }

  if (CallStatus.FAILED === call.status) {
    let whoDoneIt = '';
    if (call.incoming) {
      if (call.receiver?.errorCode) {
        whoDoneIt = 'Guide';
      }
      if (call.originator?.errorCode) {
        whoDoneIt = 'Member';
      }
    } else {
      if (call.receiver?.errorCode) {
        whoDoneIt = 'Member';
      }
      if (call.originator?.errorCode) {
        whoDoneIt = 'Guide';
      }
    }

    let tooltipText: string = null;
    if (call.status === CallStatus.FAILED) {
      tooltipText = 'Call Failed';
      if (call.receiver?.errorCode) {
        const message =
          twilioVoiceErrorMap[call.receiver.errorCode]?.message ||
          'Unknown Error. Please contact support.';
        tooltipText = `${call.receiver.errorCode}: [${whoDoneIt} Connection] ${message}`;
      }
      if (call.originator?.errorCode) {
        const message =
          twilioVoiceErrorMap[call.originator.errorCode]?.message ||
          'Unknown Error. Please contact support.';
        tooltipText = `${call.originator.errorCode}: [${whoDoneIt} Connection] ${message}`;
      }
    }

    statusChips.push(
      <Tooltip title={tooltipText} arrow={true} placement={'bottom-start'}>
        <div>
          <TaggableChip isTag color="error" label="Failed" />
        </div>
      </Tooltip>
    );
  }

  if (call.recording?.wentToVoicemail) {
    statusChips.push(
      <Tooltip
        title="Went to voicemail"
        arrow={true}
        placement={'bottom-start'}
      >
        <div>
          <TaggableChip
            color="neutral"
            label="Voicemail was left"
            icon={<VoicemailIcon weight="bold" />}
            isTag
          />
        </div>
      </Tooltip>
    );
  }

  const getSubject = () => {
    if (call.isFeedbackCall) {
      return (
        <>
          {call.incoming ? 'Incoming' : 'Outgoing'} call
          <SmsFailedIcon
            sx={{
              color: reemaColors.pacificPleasure,
              position: 'relative',
              top: '6px',
              left: '4px',
            }}
          />
        </>
      );
    } else {
      let subject = call.incoming ? 'Incoming call' : 'Outgoing call';
      if (isResourcing) {
        subject = 'Resourcing call';
      }
      return subject;
    }
  };

  if (isLastEventItem) {
    secondaryOption = (
      <Button
        variant="text"
        debounced
        onClick={() => createCall({ user: member })}
      >
        Call now
      </Button>
    );
  }

  const expandedContent = call.note
    ? HtmlDisplayerWithHyperlinks({
        note: call.note,
        displayMessage: 'Links in these call notes:',
      })
    : null;

  let outcomeString = 'Unknown';
  if (outcome) {
    outcomeString = outcome.text;
  }
  const getDescription = () => {
    switch (call.status) {
      case CallStatus.CONNECTING:
        return `Connecting...`;
      case CallStatus.FAILED:
        return `Failed, ${outcomeString}`;
      case CallStatus.MISSED:
        return `Missed, ${outcomeString}`;
      case CallStatus.BUSY:
        return `Busy, ${outcomeString}`;
      case CallStatus.ACTIVE:
        return `Active, ${outcomeString}`;
      case CallStatus.ENDED:
        return `Completed, ${outcomeString}`;
    }
  };

  const shouldShowStatusChangeRequest = canMakeRequest(
    call.timeStarted,
    selectedMember
  );

  if (isAdmin || shouldShowStatusChangeRequest) {
    menuItems.push({
      title: 'Request status change',
      icon: shouldShowStatusChangeRequest ? null : (
        <AdminPanelSettingsIcon fontSize="small" />
      ),
      details: 'Request status change',
      handler: () => {
        void openMemberStatusChangeRequestDialog({
          memberId: member.id,
          interactionId: call.interactionId,
          effectiveAt: call.timeEnded || call.timeStarted,
        });
      },
    });
  }
  menuItems.push({
    title: 'Copy link to call',
    details: 'Copy link to call',
    handler: async () => {
      const url = `${
        window.location.origin + window.location.pathname
      }?feedItemExternalType=call&feedItemExternalId=${callId}`;
      await navigator.clipboard.writeText(url);
    },
  });

  if (call.isFeedbackCall && canManageFeedbackCalls && call.incoming) {
    menuItems.push({
      title: 'Return feedback call',
      details: 'Return feedback call',
      handler: () => {
        void createCall({
          user: member,
          isFeedbackCall: true,
          highlightedNumber: call.originator.phoneNumber,
        });
      },
    });
  }

  if (isAdmin) {
    menuItems.push({
      title: 'Go to Twilio log',
      details: 'Go to Twilio log',
      icon: <AdminPanelSettingsIcon fontSize="small" />,
      handler: () => {
        const url = `https://www.twilio.com/console/voice/calls/logs/${call.sid}`;
        window.open(url, '_blank', 'noopener noreferrer');
      },
    });
    menuItems.push({
      title: 'Copy call json to clipboard',
      icon: <AdminPanelSettingsIcon fontSize="small" />,
      handler: () => {
        void navigator.clipboard.writeText(JSON.stringify(call));
      },
    });
    if (call.recording?.fileId) {
      menuItems.push({
        title: 'Listen to call recording',
        icon: <AdminPanelSettingsIcon fontSize="small" />,
        handler: async () => {
          const response = await makeRequest(filesRequests.getSignedUrl, {
            params: { id: call.recording.fileId },
          });
          window.open(response.temporaryUrl, '_blank');
        },
      });
    }
  }

  const feedNumber = call.incoming
    ? call.originator?.phoneNumber
    : call.receiver?.phoneNumber;
  let callExplanation = `${caller?.name || 'Unknown'} called ${
    receiver?.name || 'Unknown'
  }`;
  let resource: ResourceDto = null;
  if (isResourcing) {
    if (displayResourceInteraction) {
      resource = displayResourceInteraction.resource;
    } else if (displayNeedResourceInteraction) {
      resource = displayNeedResourceInteraction.resource;
    }
    callExplanation = `${caller?.name || 'Unknown'} called ${
      resource?.title || 'Unknown Resource'
    } for ${member?.name}`;
  }

  const primaryOption = (
    <Button
      variant="text"
      onClick={() => editCall({ call: call, user: member, need: displayNeed })}
    >
      Edit
    </Button>
  );

  const connectionLogHeader =
    !!call.receiverConnectionLogs || !!call.originatorConnectionLogs
      ? handleConnectionLogEntries(call, caller, receiver)
      : null;

  return (
    <InfoCard
      subject={getSubject()}
      variant={isResourcing ? 'resource' : 'call'}
      subVariant={call.incoming ? 'incoming' : 'outgoing'}
      shouldHighlight={shouldHighlight}
      startTime={call.timeConnected || call.timeStarted || call.timestamp}
      endTime={call.timeEnded}
      menuItems={menuItems}
      expandedContent={expandedContent}
      expandableHeaderContent={connectionLogHeader}
      expandableHeaderLabel="Connection details"
      primaryOption={primaryOption}
      secondaryOption={secondaryOption}
      {...((!!call.recording?.recordingTranscription ||
        !!call.recording?.summary) &&
        !needsCallFF && {
          additionalAction: (
            <CardActions disableSpacing sx={{ marginLeft: 'auto' }}>
              <MuiButton
                variant="text"
                onClick={() => setVoiceCallId(call.id)}
                {...(generateTrackingProps
                  ? generateTrackingProps(
                      `view-${
                        callDataQualityFF ? 'summary' : 'transcript'
                      }-button`
                    )
                  : {})}
              >
                {callDataQualityFF ? 'View summary' : 'View transcript'}
              </MuiButton>
            </CardActions>
          ),
        })}
      statusChips={statusChips}
    >
      <>
        <Typography
          variant="labelLarge"
          component="p"
          sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
        >
          {callExplanation}
        </Typography>
        <Typography
          variant="bodyMedium"
          component="p"
          sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
        >
          {phoneFormatter.stringToPretty(feedNumber)}
        </Typography>
        <Typography
          variant="bodyMedium"
          component="p"
          sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
        >
          {getDescription()}
        </Typography>

        {displayNeed && (
          <>
            <Typography variant="bodyLarge" component="p">
              Need:
            </Typography>
            <NeedMiniInfoBox need={displayNeed} />
          </>
        )}

        {call.voicemailFileId && (
          <Box sx={{ marginTop: '16px' }}>
            <FeedVoicemail
              fileId={call?.voicemailFileId}
              transcription={call.voicemailTranscription}
            />
          </Box>
        )}
      </>
    </InfoCard>
  );
};

export default FeedCallCard;
